import logger from "@/utilities/logger";
import { API_BASE_URL } from './Base';
import axios from 'axios';

// fetchMe in-flight 공유 — guard/guest가 같은 라우트 진입에서 동시에 호출해도
// /admin/me 네트워크 호출은 1회로 합친다. 응답이 끝나는 즉시 null로 비워 다음
// 라우트 진입에서는 새로 fetch (캐시 아님, 단순 dedupe).
let inFlightFetchMe = null;

export class Token {
    async refreshToken() {
        const axiosAPI = axios.create({
            baseURL: API_BASE_URL,
            withCredentials: true,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        const data = await axiosAPI.post('admin/token').then((r) => r.data);
        return data;
    }

    // 서버가 인정한 관리자 세션 여부를 /admin/me 호출로 확인.
    // 200 → 프로필 반환, 401/403 → /admin/token 1회 갱신 후 재시도, 그래도 실패면 null.
    // guard/guest에서 사용 — HttpOnly 쿠키 체계에서 진짜 권한 검증은 서버만 할 수 있음.
    // 동시 호출은 module-level inFlightFetchMe로 단일 Promise 공유 → 중복 호출 방지.
    async fetchMe() {
        if (inFlightFetchMe) return inFlightFetchMe;
        inFlightFetchMe = (async () => {
            const client = axios.create({
                baseURL: API_BASE_URL,
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            });
            try {
                const { data } = await client.get('admin/me');
                return data;
            } catch (e) {
                const status = e?.response?.status;
                if (status !== 401 && status !== 403) {
                    // 5xx/네트워크 오류는 세션 만료와 구분되어야 하므로 관측 가능하게 로그를 남김.
                    logger.error('[admin.fetchMe] unexpected error', status ?? e?.message, e);
                    return null;
                }
                try {
                    await this.refreshToken();
                    const { data } = await client.get('admin/me');
                    return data;
                } catch (retryErr) {
                    const retryStatus = retryErr?.response?.status;
                    if (retryStatus && retryStatus !== 401 && retryStatus !== 403) {
                        logger.error('[admin.fetchMe] retry failed', retryStatus, retryErr);
                    }
                    return null;
                }
            }
        })().finally(() => {
            inFlightFetchMe = null;
        });
        return inFlightFetchMe;
    }

    // 서버에 admin/logout 요청 → HttpOnly accessToken/refreshToken/tsq_authed를 모두 clearCookie.
    // Promise<boolean> 반환 — true: 서버 logout 성공, false: 네트워크/서버 실패.
    // UI는 결과와 무관하게 /login으로 이동해야 하지만, 호출부가 false 시 별도
    // 텔레메트리/사용자 안내를 분기할 수 있도록 결과를 노출.
    async logout() {
        const client = axios.create({
            baseURL: API_BASE_URL,
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
        });
        try {
            await client.post('admin/logout');
            return true;
        } catch (e) {
            logger.error('[admin.logout] request failed', e?.response?.status ?? e?.message, e);
            return false;
        }
    }
}
